/**
 * Globale Polling-Konfiguration
 * Zentraler Ort für alle Polling-Intervalle in der Anwendung
 * 
 * Verwendung in Komponenten:
 * - ServiceProviderTable: Automatische Updates der Dienstleister-Liste
 * - ContractTable: Automatische Updates der Vertrags-Status
 * - DienstleisterDetail: Polling für Vertrags-Updates
 * - DienstleisterSidebar: Polling für Dienstleister-Updates
 * - DashboardSection: Polling für Dashboard-Statistiken
 * - AdminOverviewPage: Polling für Admin-Dashboard-Statistiken
 * - OrganizationSidebar: Polling für Organisation-Updates
 * 
 * Alle Polling-Intervalle können hier zentral geändert werden.
 * Um Polling global zu deaktivieren, setzen Sie ENABLE_POLLING auf false.
 */
// Hook für React-Komponenten
import React from 'react';
export const POLLING_CONFIG = {
  // Zentrales Polling-Intervall für ALLE Komponenten
  GLOBAL_INTERVAL: 10000, // 10 Sekunden für alle
  
  // Legacy-Konfiguration (wird durch GLOBAL_INTERVAL ersetzt)
  DASHBOARD_INTERVAL: 10000,
  SERVICE_PROVIDER_INTERVAL: 10000,
  PROVIDERS_INTERVAL: 10000, // Alias für Backward-Compatibility
  COST_OVERVIEW_INTERVAL: 10000,
  ADMIN_INTERVAL: 10000, // Für Admin-Komponenten
  CONTRACTS_INTERVAL: 10000, // Für Contract-Komponenten
  
  // Polling aktivieren/deaktivieren
  ENABLE_POLLING: true,
  
  // Maximale Fehlerversuche bevor Polling gestoppt wird
  MAX_ERROR_COUNT: 3,
  
  // Verzögerung nach Fehlern (progressive backoff)
  ERROR_RETRY_DELAYS: [1000, 3000, 5000] // 1s, 3s, 5s
} as const;

// Hilfsfunktion um Polling-Konfiguration zu erhalten
export const getPollingConfig = () => POLLING_CONFIG;

// Typen für bessere Typsicherheit
export type PollingConfigKey = keyof typeof POLLING_CONFIG;

// Zentraler Polling-Manager
export class PollingManager {
  private static instance: PollingManager;
  private intervals: Map<string, NodeJS.Timeout> = new Map();
  private callbacks: Map<string, () => Promise<void>> = new Map();
  private isRunning: boolean = false;
  private masterInterval: NodeJS.Timeout | null = null;

  private constructor() {}

  static getInstance(): PollingManager {
    if (!PollingManager.instance) {
      PollingManager.instance = new PollingManager();
    }
    return PollingManager.instance;
  }

  // Callback für eine Komponente registrieren
  register(componentId: string, callback: () => Promise<void>) {
    this.callbacks.set(componentId, callback);
    
    if (!this.isRunning) {
      this.startGlobalPolling();
    }
  }

  // Callback für eine Komponente entfernen
  unregister(componentId: string) {
    this.callbacks.delete(componentId);
    
    if (this.callbacks.size === 0) {
      this.stopGlobalPolling();
    }
  }

  // Globales Polling starten
  private startGlobalPolling() {
    if (this.isRunning || !POLLING_CONFIG.ENABLE_POLLING) return;
    
    this.isRunning = true;
    
    this.masterInterval = setInterval(async () => {
      await this.executeAllCallbacks();
    }, POLLING_CONFIG.GLOBAL_INTERVAL);
  }

  // Alle registrierten Callbacks ausführen
  private async executeAllCallbacks() {
    const promises = Array.from(this.callbacks.entries()).map(async ([componentId, callback]) => {
      try {
        await callback();
      } catch (error) {
        // Stille Fehlerbehandlung - nur bei kritischen Fehlern loggen
        if (error instanceof Error && error.message.includes('Network Error')) {
          console.warn(`Netzwerk-Fehler beim Polling für ${componentId}`);
        }
      }
    });

    await Promise.allSettled(promises);
  }

  // Globales Polling stoppen
  private stopGlobalPolling() {
    if (!this.isRunning) return;
    
    this.isRunning = false;
    
    if (this.masterInterval) {
      clearInterval(this.masterInterval);
      this.masterInterval = null;
    }
  }

  // Manuellen Refresh für alle Komponenten triggern
  refreshAll() {
    this.executeAllCallbacks();
  }

  // Sofortiger Refresh mit optionaler Filterung nach Komponenten-IDs
  async refreshComponents(componentIds?: string[]) {
    if (!componentIds) {
      // Alle Komponenten refreshen
      await this.executeAllCallbacks();
      return;
    }

    // Nur spezifische Komponenten refreshen
    const promises = componentIds.map(async (componentId) => {
      const callback = this.callbacks.get(componentId);
      if (callback) {
        try {
          await callback();
        } catch (error) {
          // Stille Fehlerbehandlung
        }
      }
    });

    await Promise.allSettled(promises);
  }

  // Status-Info für Debugging
  getStatus() {
    return {
      isRunning: this.isRunning,
      registeredComponents: Array.from(this.callbacks.keys()),
      interval: POLLING_CONFIG.GLOBAL_INTERVAL
    };
  }
}


export const useGlobalPolling = (componentId: string, pollingCallback: () => Promise<void>) => {
  const pollingManager = PollingManager.getInstance();
  
  // Beim Mount registrieren
  React.useEffect(() => {
    pollingManager.register(componentId, pollingCallback);
    
    // Beim Unmount deregistrieren
    return () => {
      pollingManager.unregister(componentId);
    };
  }, [componentId, pollingCallback]);

  return {
    refreshAll: () => pollingManager.refreshAll(),
    getStatus: () => pollingManager.getStatus()
  };
}; 